import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, noop } from 'lodash';
import { ContinueAnyway, SubmitAnyway } from 'modules/campaigns/components';
import { urlValidations } from 'modules/campaigns/config/validations/creative';
import { useFormDataContext, useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { addProtocol } from 'modules/taboola-common-frontend-modules/utils/urlUtils';
import { useFormValidatedValue } from 'modules/taboola-common-frontend-modules/validations';
import { useValidationContext } from 'modules/taboola-common-frontend-modules/validations/ValidationContext';

export const useUrlValidatedInput = ({
    field = 'url',
    validations: validationsProp = urlValidations,
    validationDependencies: validationDependenciesProp,
    isResetFieldToInitialOnUnmount = false,
    onSubmitAnyway: onSubmitAnywayProp = noop,
    onContinueAnyway: onContinueAnywayProp = noop,
    afterOnBlur,
    bypassField,
    addHttpsProtocol = true,
}) => {
    const { validationService } = useValidationContext();
    const {
        formAccount: { accountId: formAccountId },
    } = useFormDataContext();
    const { value: bypassUrlResponseValidation, onChange: setBypassUrlResponseValidation } = useFormFieldValue({
        field: bypassField,
    });
    const submitAnyway = useMemo(
        () => (
            <SubmitAnyway
                onClick={() => {
                    setBypassUrlResponseValidation(true);
                    onSubmitAnywayProp();
                }}
            />
        ),
        [onSubmitAnywayProp, setBypassUrlResponseValidation]
    );

    const continueAnyway = useMemo(() => <ContinueAnyway onClick={onContinueAnywayProp} />, [onContinueAnywayProp]);

    const accessibleUrlCache = useRef();
    const validations = useMemo(() => validationsProp, [validationsProp]);
    const validationDependencies = useMemo(
        () => ({
            ...validationDependenciesProp,
            formAccountId,
            bypassUrlResponseValidation,
            accessibleUrlCache,
            submitAnyway,
            continueAnyway,
        }),
        [validationDependenciesProp, formAccountId, bypassUrlResponseValidation, submitAnyway, continueAnyway]
    );
    const {
        value: url,
        onChange: onChangeUrl,
        indicationData,
        isValidationsRunning,
        validationId,
        resetFieldToInitial,
        isValid,
    } = useFormValidatedValue({
        field,
        validations,
        validationDependencies,
    });

    useEffect(() => {
        return () => isResetFieldToInitialOnUnmount && resetFieldToInitial();
    }, [isResetFieldToInitialOnUnmount, resetFieldToInitial]);

    const [cacheValue, setCacheValue] = useState(url);
    const [showSuccessIcon, setShowSuccessIcon] = useState(false);
    const onChange = useCallback(value => {
        setShowSuccessIcon(false);
        setCacheValue(value);
    }, []);
    const onBlur = useCallback(() => {
        const transformedCachedValue = addHttpsProtocol ? addProtocol(cacheValue) : cacheValue;
        onChangeUrl(transformedCachedValue);

        if (!afterOnBlur) {
            return;
        }

        afterOnBlur();
    }, [addHttpsProtocol, afterOnBlur, cacheValue, onChangeUrl]);

    useEffect(() => {
        const unlisten = validationService.onValid(validationId, () => {
            if (isEmpty(cacheValue)) {
                return;
            }
            setShowSuccessIcon(true);
        });

        return unlisten;
    }, [validationService, validationId, cacheValue, setShowSuccessIcon]);

    return {
        cacheValue,
        onChange,
        onBlur,
        showSuccessIcon,
        isValidationsRunning,
        validationId,
        isValid,
        indicationData,
    };
};
